import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import Button from "../../../../components/NewDesign/Button/Button";
import SearchBar from "../../../../components/NewDesign/SearchBar/SearchBar";
import CircularProgressBar from "../../../../components/NewDesign/StatusBar/CircularProgressBar";
import { TagWithIcon } from "../../../../components/NewDesign/Tags";
import {
  ChangeFilled,
  ChevronRightOutlined,
  CloseOutlined,
  DoubleTickOutlined,
  ExclamationIcon,
  MailFilled,
  PhoneFilled,
  TagFilled,
  TickOutlined,
} from "../../../../components/OldDesign/Icons";
import { BulbIllustration } from "../../../../components/NewDesign/Illustrations";
import SectionTitle from "../../../../components/NewDesign/SectionTitle";
import { Cell, Pie, PieChart } from "recharts";

import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { getJobPosition, getNetworkConnections } from "../../../../api/endpoints";
import Loading from "../../../../components/OldDesign/Loading";
import {
  ACTION_SUGGESTIONS,
  COMPATIBILITY_STAT_COLORS,
  JOBPOS_CORECV_FIELD_KEYS,
  SCORE_TAGS,
} from "../../../../components/NewDesign/constants";
import MatchedOrientationCard from "../../../../components/NewDesign/MatchedOrientationCard";
import { useStore } from "../../../../store/useStore";
import Tooltip from "../../../../components/NewDesign/Tooltips/Tooltip";
import { useTranslation } from "react-i18next";
import Drawer from "components/NewDesign/Drawer/Drawer";
import { Wordcloud } from "@visx/wordcloud";
import useIsMobile from "utils/useIsMobile";
import MobileAnalysisReport from "./MobileAnalysisReport";

const AnalysisReport = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [analysisReport, setAnalysisReport] = useState({ data: {}, isLoading: false, isLoaded: false });
  const [currentMatchId, setCurrentMatchId] = useState(null);
  const matches = analysisReport?.data?.match || [];
  const currentMatch = useMemo(() => {
    if (!currentMatchId || !Array.isArray(matches)) return null;
    return matches.find((match) => match?.info_target?.id === currentMatchId) || null;
  }, [currentMatchId, matches]);
  const currentMatchIndex = matches.findIndex((match) => match.info_target.id === currentMatchId);
  const [searchKeyword, setSearchKeyword] = useState("");
  const isUserReport = location.state.reportType === "user";
  const isMobile = useIsMobile();
  const [aidData, setAidData] = useState(null);
  const [metaCv, setMetaCv] = useState(null);
  const strapiContent = useStore((state) => state.strapiContent);

  const modalScrollY = useStore((state) => state.modalScrollY);

  const jobPosCorecvFields = JOBPOS_CORECV_FIELD_KEYS();

  function showTooltip(evt, text) {
    let tooltip = document.getElementById("tooltip");
    tooltip.innerHTML = text;
    tooltip.style.display = "block";
    tooltip.style.left = evt.clientX > 150 ? evt.clientX - 130 + "px" : evt.clientX - 20 + "px";
    tooltip.style.top = evt.clientY + 10 + "px";
  }

  function hideTooltip() {
    var tooltip = document.getElementById("tooltip");
    tooltip.style.display = "none";
  }

  const handleSearch = (e) => {
    setSearchKeyword(e.target.value);
  };

  useEffect(() => {
    if (location.state.reportType === "user") {
      getNetworkConnections(
        null,
        `/${id}/match`,
        (res) => {
          setAnalysisReport((prev) => {
            return { ...prev, data: res.data, isLoaded: true };
          });
          const selectedIndex = res.data.match.findIndex((item) => item.info_target.id === location.state.posId);
          setCurrentMatchId(
            selectedIndex >= 0 ? res.data.match[selectedIndex].info_target.id : res.data.match[0]?.info_target.id,
          );
        },
        (err) => console.log(err),
        (bool) =>
          setAnalysisReport((prev) => {
            return { ...prev, isLoading: bool };
          }),
      );
    } else
      getJobPosition(
        null,
        `/${id}/match`,
        (res) => {
          setAnalysisReport((prev) => {
            return { ...prev, data: res.data, isLoaded: true };
          });
          setCurrentMatchId(res.data.match[0]?.info_target.id);
        },
        (err) => console.log(err),
        (bool) =>
          setAnalysisReport((prev) => {
            return { ...prev, isLoading: bool };
          }),
      );
  }, [id, location]);

  useEffect(() => {
    setMetaCv(null);
    if (!currentMatchId) return;
    getNetworkConnections(
      null,
      `/${currentMatchId}`,
      (res) => {
        setMetaCv(res.data.rows[0]);
        const _aid = strapiContent.data.find((aid) => aid.codeName === res.data.rows[0]?.meta_cv?.aid?.id);
        setAidData(_aid);
      },
      (err) => console.log(err),
      // (bool) => console.log("Loading state:", bool),
    );
  }, [currentMatchId]);

  if (analysisReport.isLoading || strapiContent.isLoading) {
    return <Loading />;
  }
  if (!analysisReport.isLoaded || !strapiContent.isLoaded) {
    return null;
  }

  return (
    <>
      {isMobile ? (
        <MobileAnalysisReport
          isUserReport={isUserReport}
          analysisReport={analysisReport}
          matches={matches}
          _aid={aidData}
          metaCv={metaCv}
          currentMatch={currentMatch}
          setCurrentMatchId={setCurrentMatchId}
        />
      ) : currentMatch ? (
        <div className="w-[1356px] bg-neutral-200 max-lg:w-screen">
          {/* header */}
          <div className="mb-6 flex items-center justify-between bg-neutral-100 px-9 py-4 max-lg:mb-3 max-lg:p-4 ">
            <h2 className="text-[26px] font-bold leading-tight text-primary-900 max-lg:text-[14px]">
              {currentMatch?.info_reference.name} {t("modal.report.compatibility_report")}
            </h2>
            <Button onClick={() => navigate(-1)} className="bg-transparent text-primary-600">
              {t("button.exit")}
            </Button>
          </div>
          <div className="relative flex gap-6 max-lg:flex-col lg:pl-9">
            {/* main */}
            <div className="relative flex basis-2/3 flex-col gap-4 max-lg:px-4">
              {/* top ID card */}
              <div
                className={`${
                  modalScrollY > 160 && "sticky top-0"
                } z-[51] flex justify-between rounded-xl border border-primary-500 bg-primary-75 px-3 py-4 max-lg:flex-col max-lg:gap-2`}
              >
                <div className="flex items-center gap-1">
                  <CircularProgressBar value={currentMatch?.match_percent} small />
                  <div className="flex flex-col items-start">
                    <p className="text-lg font-bold leading-tight text-primary-900">{currentMatch?.info_target.name}</p>
                    {metaCv && (
                      <p className="leadting-tight text-sm font-normal text-neutral-700">
                        {metaCv?.work?.job[0]?.name} &#x2022; {metaCv?.basic?.state[0]?.name},
                        {metaCv?.basic?.country[0]?.name}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex flex-wrap items-center gap-1">
                    <TagWithIcon
                      className={`${
                        SCORE_TAGS[currentMatch.score_tag.id].color
                      } border border-neutral-700 bg-transparent font-medium`}
                      icon={SCORE_TAGS[currentMatch.score_tag.id].icon}
                      text={currentMatch.score_tag.name}
                    />
                    {!isUserReport && (
                      <TagWithIcon
                        className={`${
                          currentMatch?.score_tag.type === "employee"
                            ? "bg-primary-75 text-primary-600"
                            : "bg-secondary_applicant-100 text-secondary_applicant-600"
                        } border border-neutral-700 font-medium`}
                        text={
                          currentMatch?.score_tag.type === "employee" ? t("common.employee") : t("common.candidate")
                        }
                      />
                    )}
                  </div>
                  {metaCv && (
                    <div className="flex items-center gap-2">
                      <p className="text-prim9ary-900 text-sm font-normal leading-tight">
                        <MailFilled className="inline-block h-4 w-4 text-secondary_recruiter-700" />
                        <span className="pl-1">{metaCv?.communications?.email[0]?.name}</span>
                      </p>
                      <p className="text-prim9ary-900 text-sm font-normal leading-tight">
                        <PhoneFilled className="inline-block h-4 w-4 text-secondary_recruiter-700" />
                        <span className="pl-1">{metaCv?.communications?.phone[0]?.name || "n/a"}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
              {/* action recommendations */}
              <ActionsSuggestionsBox currentMatch={currentMatch} />
              {/* wordcloud & aid */}
              <div className="flex gap-4 rounded-xl bg-neutral-100 p-6 max-lg:p-4">
                {metaCv ? (
                  <>
                    <div className="flex-1 rounded-xl border p-4 max-lg:p-3">
                      <SectionTitle
                        title={t("modal.metacv.personality_type")}
                        className="items-start gap-0 text-base"
                      />
                      <div className="flex flex-col gap-2">
                        <div className="mx-auto">
                          <div
                            id="tooltip"
                            // display="none"
                            className="fixed z-[999] hidden max-w-[360px] rounded-lg bg-primary-800 p-2 text-justify text-sm text-neutral-100"
                          ></div>
                          <Wordcloud
                            words={metaCv.meta_cv.word_cloud}
                            fontSize={(word) => (Math.sqrt(word.value) * 3) / 4}
                            padding={8}
                            font={"Inter"}
                            rotate={(word) => 0}
                            spiral="archimedean"
                            width={isMobile ? 325 : 350}
                            height={isMobile ? 260 : 200}
                            random={() => 0.5}
                          >
                            {(cloudWords) =>
                              cloudWords.map((word, index) => {
                                return (
                                  <Fragment key={index}>
                                    <defs key={index}>
                                      <filter x="-4%" y="-5%" width="108%" height="110%" id={index.toString()}>
                                        <feFlood floodColor={word.bgColor} />
                                        <feGaussianBlur stdDeviation="2" />
                                        <feComponentTransfer>
                                          <feFuncA type="table" tableValues="0 0 0 1" />
                                        </feComponentTransfer>

                                        <feComponentTransfer>
                                          <feFuncA type="table" tableValues="0 1 1 1 1 1 1 1" />
                                        </feComponentTransfer>
                                        <feComposite operator="over" in="SourceGraphic" />
                                      </filter>
                                    </defs>
                                    <text
                                      style={{
                                        fontSize: word.size,
                                        fontWeight: Math.log(word.value) * 100,
                                        fill: word.color,
                                        fontFamily: word.font,
                                        cursor: "help",
                                      }}
                                      filter={`url(#${index})`}
                                      textAnchor="middle"
                                      transform={`translate(${word.x}, ${word.y}) rotate(${word.rotate})`}
                                      onMouseMove={(e) => showTooltip(e, word.explain[0].description)}
                                      onMouseLeave={hideTooltip}
                                    >
                                      {word.text}
                                    </text>
                                  </Fragment>
                                );
                              })
                            }
                          </Wordcloud>
                        </div>
                      </div>
                      <hr className="py-2"></hr>
                      <div className="flex flex-wrap justify-center gap-2">
                        {metaCv.meta_cv.upper_dimensions.map((upType, index) => {
                          return (
                            <Tooltip key={index} content={upType.description}>
                              <div
                                className="flex cursor-help items-center justify-center gap-1 rounded-sm p-1"
                                style={{ backgroundColor: upType.color + "20", color: upType.color }}
                              >
                                <span className={`text-xs max-lg:text-[8px]`}>{"\u2B24"}</span>
                                <p className="text-xs font-semibold">{upType.name}</p>
                              </div>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col gap-4 rounded-xl border p-4 max-lg:p-3">
                      <SectionTitle title={t("modal.metacv.humanas_id")} className="items-start gap-0 text-base" />

                      <div className="flex flex-col justify-center gap-2">
                        <div className="flex items-center gap-4">
                          <Link to={`/aid/identities/${aidData.codeName}`}>
                            <div
                              style={{
                                background: `linear-gradient(180deg, ${aidData.primaryColor} 0%, #FFFFFF00 100%)`,
                              }}
                              className={`h-[100px] w-[100px] rounded-full p-2 shadow-[inset_0px_10px_8px_3px_#00000018] max-lg:h-[80px] max-lg:w-[80px]`}
                            >
                              <img src={aidData.logo.url} alt={aidData.name} title={aidData.name} />
                            </div>
                          </Link>
                          <div className="inline-flex flex-1 flex-col gap-1">
                            <p className="text-lg font-bold leading-tight text-primary-900">{aidData.name}</p>
                            <p className="text-sm font-medium leading-tight text-neutral-900">{aidData.title}</p>
                          </div>
                        </div>
                        <p className="text-sm leading-tight text-primary-900">{aidData.definition}</p>
                      </div>
                      <hr className="mt-auto"></hr>
                      <div className="scrollbar-hide flex gap-3 overflow-x-auto max-lg:gap-1.5">
                        {aidData.keyword.map((keyword, index) => (
                          <TagWithIcon
                            className={"bg-primary-75 text-primary-800"}
                            key={index}
                            text={keyword.keyword}
                          />
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <Loading className={"h-[350px]"} />
                )}
              </div>
              {/* professional orientation summary */}
              <div className="rounded-xl bg-neutral-100 p-6 max-lg:p-4">
                <SectionTitle
                  title={t("modal.metacv.occupational_orientation") + " " + t("common.summary")}
                  className="items-start gap-0 text-[22px] max-lg:text-base"
                />
                <CompatibilitySummary summary={currentMatch?.dimensions_compatibility} />
              </div>
              {/* Core CV */}
              <div className="flex flex-col gap-4 rounded-xl bg-neutral-100 px-6 py-3 max-lg:p-3">
                <div className="flex items-center justify-between">
                  <SectionTitle title={"Core CV"} className="items-start gap-0 text-[22px] max-lg:text-base" />
                  <Tooltip
                    content={
                      <div className="flex flex-col items-start gap-1">
                        {Object.keys(analysisReport.data.core_cv_states_content).map((key, index) => {
                          return (
                            <CoreCvTag
                              key={index}
                              text={analysisReport.data.core_cv_states_content[key].description}
                              variant={index}
                            />
                          );
                        })}
                      </div>
                    }
                  >
                    <TagWithIcon
                      className={"border border-secondary_recruiter-600 bg-opacity-0 text-secondary_recruiter-600"}
                      icon={<TagFilled className={"g-3 w-3"} />}
                      text={"Tag Info"}
                    />
                  </Tooltip>
                </div>
                {/* personal info */}
                <div className="rounded-xl border p-4 max-lg:p-3">
                  <SectionTitle
                    title={t("candidate.corecv_form.user_profile")}
                    className="items-start gap-0 text-base"
                  />
                  <div className="flex flex-col divide-y-2">
                    {["job_title", "location", "age_range", "work_state", "about"].map((key, index) => {
                      const field = currentMatch?.core_cv.find((item) => item.id === key);
                      return (
                        <div className="flex gap-4 py-3" key={index}>
                          <div className="w-[200px] shrink-0">
                            <p className="text-sm font-semibold leading-tight text-primary-900">
                              {jobPosCorecvFields[field.id]}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {field &&
                              field.data.map((item, index) => (
                                <CoreCvTag
                                  key={index}
                                  text={item.name}
                                  variant={item.compatibility}
                                  tagContent={
                                    analysisReport.data.core_cv_states_content[item.compatibility].description
                                  }
                                />
                              ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* professional info */}
                <div className="rounded-xl border p-4 max-lg:p-3">
                  <SectionTitle
                    title={t("candidate.corecv_form.professional_info")}
                    className="items-start gap-0 text-base"
                  />
                  <div className="flex flex-col divide-y-2">
                    {["job_title_experience", "work_time", "work_location", "experiences", "skills"].map(
                      (key, index) => {
                        const field = currentMatch?.core_cv.find((item) => item.id === key);
                        return (
                          <div className="flex gap-4 py-3" key={index}>
                            <div className="w-[200px] shrink-0">
                              <p className="text-sm font-semibold leading-tight text-primary-900">
                                {jobPosCorecvFields[field.id]}
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {field && key !== "experiences"
                                ? field.data.map((item, index) => (
                                    <CoreCvTag
                                      key={index}
                                      text={item.name}
                                      variant={item.compatibility}
                                      tagContent={
                                        analysisReport.data.core_cv_states_content[item.compatibility].description
                                      }
                                    />
                                  ))
                                : // corecv experience
                                  field.elements.map((item, index) => (
                                    <div className="flex" key={index}>
                                      <p className="text-sm font-medium leading-tight text-primary-800">
                                        <span>•</span>
                                        {item.company.name},
                                      </p>

                                      <p className="pl-1 text-sm font-medium leading-tight text-neutral-700">
                                        {item.jobs[0].name}, {item.start} - {item.end}
                                      </p>
                                    </div>
                                  ))}
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
                {/* education info */}
                <div className="rounded-xl border p-4 max-lg:p-3">
                  <SectionTitle
                    title={t("candidate.corecv_form.education_info")}
                    className="items-start gap-0 text-base"
                  />
                  <div className="flex flex-col divide-y-2">
                    {["degree", "universities", "university_departments", "languages"].map((key, index) => {
                      const field = currentMatch?.core_cv.find((item) => item.id === key);
                      return (
                        <div className="flex gap-4 py-3" key={index}>
                          <div className="w-[200px] shrink-0">
                            <p className="text-sm font-semibold leading-tight text-primary-900">
                              {jobPosCorecvFields[field.id]}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {field &&
                              field.data.map((item, index) => (
                                <CoreCvTag
                                  key={index}
                                  text={item.name}
                                  variant={item.compatibility}
                                  tagContent={
                                    analysisReport.data.core_cv_states_content[item.compatibility].description
                                  }
                                />
                              ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              {/* professional orientations box */}
              <div className="rounded-xl bg-neutral-100 px-6 py-3 max-lg:p-3">
                <SectionTitle
                  title={t("modal.metacv.occupational_orientation")}
                  className="items-start max-lg:text-base"
                />
                <div className="flex flex-col gap-2">
                  {currentMatch?.dimensions.map((dimension, index) => {
                    return (
                      <MatchedOrientationCard
                        key={index}
                        refName={currentMatch.info_reference.name}
                        targetName={currentMatch.info_target.name}
                        orientation={dimension}
                        orientationContent={analysisReport.data.dimensions_content[dimension.id_combined]}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
            {/* members list */}
            <MembersBox
              matches={matches}
              handleSearch={handleSearch}
              searchKeyword={searchKeyword}
              currentMatchIndex={currentMatchIndex}
              setCurrentMatchId={setCurrentMatchId}
              currentMatchId={currentMatchId}
              isUserReport={isUserReport}
            />
          </div>
        </div>
      ) : (
        <Loading className={"h-[500px]"} />
      )}
    </>
  );
};

const MembersBox = ({
  matches,
  handleSearch,
  searchKeyword,
  currentMatchIndex,
  setCurrentMatchId,
  currentMatchId,
  isUserReport,
}) => {
  const { t } = useTranslation();
  const modalScrollY = useStore((state) => state.modalScrollY);
  const [memberTypeFilter, setMemberTypeFilter] = useState("");
  const handleMemberTypeFilterChange = (type) => {
    if (memberTypeFilter === "" || memberTypeFilter !== type) {
      setMemberTypeFilter(type);
    } else if (memberTypeFilter === type) {
      setMemberTypeFilter("");
    }
  };
  return (
    <div
      className={`${
        modalScrollY > 160 && "sticky top-0"
      } flex max-h-screen basis-1/3 flex-col gap-4 self-start overflow-y-auto rounded-l-xl bg-neutral-100 p-6 max-lg:hidden`}
    >
      <div className="flex flex-col gap-2">
        <h3 className="text-xl font-bold leading-tight text-primary-900">{t("placeholder.members")}</h3>
        <p className="leading-tight text-neutral-700">{t("modal.report.select_user_to_compare")}</p>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <SearchBar onChange={handleSearch} />
        </div>
        <div className="flex gap-2">
          <TagWithIcon
            className={`${
              memberTypeFilter === "candidate"
                ? "bg-secondary_recruiter-600 text-neutral-100"
                : " bg-neutral-100 text-secondary_recruiter-600"
            }  cursor-pointer border border-secondary_recruiter-600`}
            text={`${t("common.candidate")} \u00B7 ${
              matches.filter((match) => match.score_tag.type === "candidate").length
            }`}
            onClick={() => {
              handleMemberTypeFilterChange("candidate");
            }}
          />
          <TagWithIcon
            className={`${
              memberTypeFilter === "employee"
                ? "bg-secondary_recruiter-600 text-neutral-100"
                : " bg-neutral-100 text-secondary_recruiter-600"
            }  cursor-pointer border border-secondary_recruiter-600`}
            text={`${t("common.employee")} \u00B7 ${
              matches.filter((match) => match.score_tag.type === "employee").length
            }`}
            onClick={() => {
              handleMemberTypeFilterChange("employee");
            }}
          />
        </div>
      </div>
      <ul className="flex flex-col gap-3">
        {matches
          .filter((match) => match.info_target.name.toLowerCase().includes(searchKeyword.toLowerCase()))
          .filter((match) => match.score_tag.type.includes(memberTypeFilter))
          .map((match, index) => (
            <button onClick={() => setCurrentMatchId(match.info_target.id)} key={index}>
              <MatchRow
                isUserReport={isUserReport}
                match={match}
                isSelected={match.info_target.id === currentMatchId}
                variant={match.score_tag.id}
              />
            </button>
          ))}
      </ul>
    </div>
  );
};

const ActionsSuggestionsBox = ({ currentMatch }) => {
  const { t } = useTranslation();
  const actionsSuggestions = ACTION_SUGGESTIONS();
  return (
    <div className="rounded-xl bg-neutral-100 px-6 py-3 max-lg:px-4">
      <div className="flex items-center gap-1.5">
        <BulbIllustration className={"w-8"} />
        <h2 className="text-[22px] font-semibold text-secondary_recruiter-800 max-lg:text-base">
          {t("modal.report.action_suggestions")}
        </h2>
      </div>
      <div className={`flex flex-col gap-2 overflow-auto transition-all duration-300 ease-in max-lg:text-[13px]`}>
        {actionsSuggestions.map((action, index) => {
          const suggestions = currentMatch.action_suggestions.filter((item) => item.type === action.type);
          if (!suggestions.length) {
            return null;
          }
          return <Drawer key={index} action={action} index={index} suggestions={suggestions} />;
        })}
      </div>
    </div>
  );
};

const MatchRow = ({ match, isSelected, variant, isUserReport }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`${
        isSelected ? "border-primary-500 bg-primary-75" : "border-neutral-300"
      } flex flex-col items-start gap-2 rounded-xl border p-3 transition-all`}
    >
      <div className="flex items-center gap-1">
        <CircularProgressBar value={match.match_percent} small />
        <p>{match.info_target.name}</p>
      </div>
      <div className="flex flex-wrap gap-1">
        <TagWithIcon
          className={`${SCORE_TAGS[variant].color} border border-neutral-700 bg-transparent font-medium`}
          icon={SCORE_TAGS[variant].icon}
          text={match.score_tag.name}
        />
        {!isUserReport && (
          <TagWithIcon
            className={`${
              match.score_tag.type === "employee"
                ? "bg-primary-75 text-primary-600"
                : "bg-secondary_applicant-100 text-secondary_applicant-600"
            } border border-neutral-700 font-medium`}
            text={match.score_tag.type === "employee" ? t("common.employee") : t("common.candidate")}
          />
        )}
      </div>
    </div>
  );
};

const CompatibilitySummary = ({ summary }) => {
  const metacvDimStats = summary;
  const colors = COMPATIBILITY_STAT_COLORS;
  const RADIAN = Math.PI / 180;
  const customLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <>
        {percent > 0 && (
          <>
            <circle cx={x} cy={y} r={13} fill="white" opacity="0.5" />
            <text x={x} y={y} fill="#0D568A" fontWeight={600} textAnchor={"middle"} dominantBaseline="central">
              {`${(percent * 14).toFixed(0)}`}
            </text>
          </>
        )}
      </>
    );
  };

  const handleScrollToOrientation = (id) => {
    let el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="flex items-center justify-center gap-3">
      <PieChart width={225} height={225}>
        <Pie
          data={metacvDimStats}
          dataKey={"value"}
          nameKey={"title"}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={customLabel}
          outerRadius={100}
          innerRadius={60}
        >
          {metacvDimStats.map((entry, index) => (
            <Cell key={index} fill={colors[entry.id]} strokeWidth={0} />
          ))}
        </Pie>
      </PieChart>
      <div className="flex w-[350px] flex-col divide-y">
        {metacvDimStats.map((entry, index) => (
          <div key={index} className="flex flex-1 items-center justify-between py-2">
            <p className="flex items-center pr-1 text-[17px] font-semibold leading-tight text-primary-700 max-lg:text-[14px]">
              <span className={`pr-2 text-[10px]`} style={{ color: colors[entry.id] }}>
                {"\u2B24"}
              </span>
              {entry.name}
            </p>
            <button
              className="flex items-center"
              // onClick={() => handleScrollToOrientation("orientationCard_" + entry.id)}
              disabled={entry}
            >
              {entry.value}
              <ChevronRightOutlined className={"h-4 w-4 text-primary-800"} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const CoreCvTag = ({ text, variant, tagContent }) => {
  const variants = {
    0: "bg-card_candidate-250 text-neutral-700",
    1: "bg-secondary_applicant-200 text-secondary_applicant-600",
    2: "bg-neutral-600 text-neutral-100",
    3: "bg-secondary_applicant-600 text-neutral-100",
    4: "bg-primary-300 text-neutral-100",
    5: "bg-primary-400 text-neutral-100",
  };
  const icons = {
    0: <ExclamationIcon className={"h-4 w-4"} />,
    1: <TickOutlined className={"h-4 w-4"} />,
    2: <CloseOutlined className={"h-4 w-4"} />,
    3: <DoubleTickOutlined className={"h-4 w-4"} />,
    4: <CloseOutlined className={"h-4 w-4"} />,
    5: <TickOutlined className={"h-4 w-4"} />,
  };
  return (
    <Tooltip content={tagContent}>
      <TagWithIcon
        icon2={icons[variant]}
        text={text}
        className={`${variants[variant]} max-w-full items-start justify-start`}
      />
    </Tooltip>
  );
};

export default AnalysisReport;
